/**
 * Shared formatting helpers for the Rent module.
 *
 * The two money formatters honour hide-values (V5-03) for the same reason
 * `formatINR` in `src/lib/utils.ts` does: they are the layer every rent screen
 * routes its rupee figures through, so masking here covers the module without
 * editing a page. Masking is applied to the STRING — `paise` in, `paise`
 * unchanged — so no rent total, dues figure or chart series moves.
 *
 * `fmtINRExport` is the deliberate escape hatch for CSV/PDF/print output,
 * where the user has explicitly asked for the numbers.
 */
import { formatAmountPlainRaw, formatAmountRaw } from "@core";
import { maskMoney } from "@/lib/privacy/mask";
import { shouldHideValues } from "@/lib/privacy/store";

export const fmtINR = (paise: number | null | undefined): string => {
  const formatted = fmtINRExport(paise);
  return shouldHideValues() ? maskMoney(formatted) : formatted;
};

/** Unmasked. For exports and printable documents only. Exact — ₹2,500.45. */
export const fmtINRExport = (paise: number | null | undefined): string =>
  formatAmountRaw(paise ?? 0);

export const fmtINRPlain = (paise: number | null | undefined): string => {
  if (shouldHideValues()) return maskMoney("");
  return formatAmountPlainRaw(paise ?? 0);
};

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function fmtDate(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return "—";
  const day = d.getDate().toString().padStart(2, "0");
  return `${day} ${monthNames[d.getMonth()]} ${d.getFullYear()}`;
}

export function fmtDateShort(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = typeof input === "string" ? new Date(input) : input;
  if (isNaN(d.getTime())) return "—";
  return `${d.getDate().toString().padStart(2, "0")} ${monthNames[d.getMonth()]}`;
}

export function daysBetween(a: string | Date, b: string | Date): number {
  const d1 = typeof a === "string" ? new Date(a) : a;
  const d2 = typeof b === "string" ? new Date(b) : b;
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
}

export function daysFromNow(date: string | Date): number {
  return daysBetween(new Date(), date);
}

export function relativeTime(date: string | Date): string {
  const days = daysFromNow(date);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  if (days > 0 && days < 30) return `in ${days} days`;
  if (days < 0 && days > -30) return `${Math.abs(days)} days ago`;
  return fmtDate(date);
}
